import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  input,
  InputSignal,
  Signal,
  signal,
  untracked,
  viewChild,
  WritableSignal,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControlStatus,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Mapper } from '@shared/helpers/mapper';
import { ExtendedEditorComponent } from '@shared/modules/rich-editor/components/extended-editor/extended-editor.component';
import { DataMode } from '@shared/modules/rich-editor/helpers/enums/data-mode.enum';
import { UnsaveChangesEvaluation } from '@shared/modules/rich-editor/interfaces/unsave-changes-evaluation';
import { RichEditorService } from '@shared/modules/rich-editor/services/rich-editor.service';
import { CurrentUserService } from '@shared/services';
import { dateRangeValidator } from '@shared/validators/date-range-validator';
import moment from 'moment';
import { Observable, timer } from 'rxjs';
import { UpdateCountryTaxNewsRequest } from 'src/app/web-api-client';
import { TaxNews } from '../../models/tax-news';
import { TaxNewsService } from '../../services/tax-news.service';

@Component({
  standalone: false,
  selector: 'gstar-tax-news-editor',
  templateUrl: './tax-news-editor.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaxNewsEditorComponent implements UnsaveChangesEvaluation {
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  private readonly translation = inject(TranslateService);
  private readonly activeRichEditor = inject(RichEditorService);
  private readonly taxNewsService = inject(TaxNewsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly currentUserService = inject(CurrentUserService);

  countryId: InputSignal<number> = input.required<number>();
  taxNewsId: InputSignal<number | undefined> = input<number | undefined>();
  isEdit: InputSignal<boolean> = input<boolean>(false);

  taxNews: WritableSignal<TaxNews> = signal({} as TaxNews);
  content: WritableSignal<string> = signal('');
  disableSaveButton: WritableSignal<boolean> = signal(false);
  canEditCountry: Signal<boolean> = computed(() =>
    this.currentUserService.canEditCountry(this.countryId())
  );
  showReadOnlyLabel: Signal<boolean> = computed(
    () => this.currentUserService.isCountryAdmin() && !this.canEditCountry()
  );

  taxNewsForm!: FormGroup;
  message: Record<'required', string> = { required: '' };

  editor: Signal<ExtendedEditorComponent | undefined> = viewChild(
    ExtendedEditorComponent
  );
  date: WritableSignal<Date[]> = signal([new Date()]);
  minDate = moment().subtract(100, 'days');
  minDateText = this.minDate.format('MM/DD/YYYY');

  private editorInitialized = false;

  constructor() {
    this.loadTitleForm();

    this.message.required = this.translation.instant(
      'countryInformation.newsTitleCantBeEmpty'
    );

    effect(() => {
      const isPermissionsReady =
        this.currentUserService.countryAdminPermissionsReady();
      const countryId = this.countryId();
      this.taxNewsId();
      this.isEdit();

      if (
        !isPermissionsReady ||
        countryId === null ||
        countryId === undefined ||
        this.editorInitialized
      ) {
        return;
      }

      this.editorInitialized = true;
      untracked(() => this.initializeEditor());
    });
  }

  goBack(): void {
    this.router.navigate([`/country/${this.countryId()}/tax-news`]);
  }

  hasUnsaveChanges(): boolean {
    return (
      !this.editor()?.hasNotChanges() ||
      this.taxNewsForm.dirty ||
      !!(this.activeRichEditor.isEmpty && this.taxNews().countryTaxNewsId)
    );
  }

  saveUnsaveChanges(html: string): void {
    if (!this.canEditCountry()) {
      return;
    }

    if (this.taxNewsForm.valid) {
      const subscription = this.save(html).subscribe(
        (taxNews: TaxNews | undefined) => {
          this.loadTaxNews(taxNews);
          this.reset();
          subscription.unsubscribe();
        }
      );
    }
  }

  saveHandler(html: string): void {
    if (!this.canEditCountry()) {
      return;
    }

    this.save(html)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((taxNews: TaxNews | undefined) => {
        this.resetViewOrRedirect();
        this.reset();
        this.loadTaxNews(taxNews);
      });
  }

  cancelHandler(): void {
    if (this.hasUnsaveChanges()) {
      const modal = this.editor()!.openUnsaveChangeWarningModal(
        this.isInvalid()
      );

      modal.instance
        .afterClosed()
        .subscribe((isConfirm: boolean | undefined) => {
          if (isConfirm) {
            if (!this.isInvalid()) {
              this.activeRichEditor.extractHTML((html: string) =>
                this.saveHandler(html)
              );
            }
          } else if (isConfirm !== undefined) {
            this.resetViewOrRedirect();
            this.reset();
            this.loadTaxNews(this.taxNews());
          }
        });
    } else {
      this.resetViewOrRedirect();
      this.reset();
      this.loadTaxNews(this.taxNews());
    }
  }

  loadTaxNews(taxNews: TaxNews | undefined): void {
    if (taxNews) {
      this.taxNews.set(taxNews);
      this.content.set(taxNews.description!);
      this.taxNewsForm.patchValue({ name: taxNews.name, date: taxNews.date });
      this.date.set([taxNews.date!, taxNews.date!]);
    }
  }

  modeUpdate(mode: DataMode): void {
    if (mode === DataMode.edition && !this.canEditCountry()) {
      this.editor()?.mode.set(DataMode.readonly);
      return;
    }

    const control = this.taxNewsForm.get('name')!;
    if (mode === DataMode.edition) {
      control.enable();
    } else {
      control.disable();
    }
  }

  private initializeEditor(): void {
    if (!this.taxNewsId() && !this.canEditCountry()) {
      this.goBack();
      return;
    }

    const control = this.taxNewsForm.get('name')!;
    if (this.taxNewsId()) {
      control.disable();
      this.taxNewsService
        .getTaxNewsById(this.taxNewsId()!, this.countryId())
        .subscribe((taxNews: TaxNews | undefined) => {
          if (taxNews) {
            this.date.set([taxNews.date!, taxNews.date!]);
            this.loadTaxNews(taxNews);
            if (this.isEdit() && this.canEditCountry()) {
              this.editor()?.edit();
            }
          }
        });
    } else {
      control.enable();
      timer(100).subscribe(() => {
        const today = moment().toDate();
        this.date.set([today, today]);
        this.taxNewsForm.patchValue({
          date: today,
        });
        if (this.canEditCountry() && this.editor()) {
          this.editor()?.mode.set(DataMode.edition);
        }
      });
    }
  }

  private save(html: string): Observable<TaxNews | undefined> {
    const request: UpdateCountryTaxNewsRequest = this.getRequest(html);
    request.newsDate = this.taxNewsForm.get('date')?.value.length
      ? this.taxNewsForm.get('date')?.value[0]
      : this.taxNewsForm.get('date')?.value;
    if (this.taxNewsId()) {
      return this.taxNewsService.updateTaxNews(
        request.countryTaxNewsId,
        request,
        this.countryId()
      );
    }
    return this.taxNewsService.addTaxNews(request, this.countryId());
  }

  private getRequest(content: string): UpdateCountryTaxNewsRequest {
    return Mapper.map(this.taxNews(), UpdateCountryTaxNewsRequest, [
      {
        description: (_source: TaxNews): string | undefined => content,
        countryIds: (): [number] | undefined => [this.countryId()],
        countryTaxNewsId: (source: TaxNews): number => source.countryTaxNewsId!,
        name: (_source: TaxNews): string =>
          this.taxNewsForm.get('name')!.value!,
        isActive: true,
      },
    ]);
  }

  private reset(): void {
    this.activeRichEditor.destroy();
    this.taxNewsForm.reset();
  }

  private resetViewOrRedirect(): void {
    if (this.taxNewsId()) {
      const control = this.taxNewsForm.get('name')!;
      control.setValue(this.taxNews().name);
      this.editor()?.mode.set(DataMode.readonly);
    } else {
      this.goBack();
    }
  }

  private loadTitleForm(): void {
    this.taxNewsForm = this.formBuilder.group({
      name: ['', Validators.required],
      date: [
        [],
        [
          Validators.required,
          dateRangeValidator({
            minDate: this.minDate.toDate(),
            maxDate: moment().toDate(),
          }),
        ],
      ],
    });

    this.taxNewsForm.valueChanges.subscribe(value => {
      if (this.editor()) {
        this.editor()?.hasNotChanges.set(
          this.editor()?.mode() === DataMode.readonly ||
            (value.name === this.taxNews().name &&
              value.date === this.taxNews().date) ||
            this.taxNewsForm.invalid ||
            this.activeRichEditor.isEmpty
        );
      }
    });

    this.taxNewsForm.statusChanges.subscribe((status: FormControlStatus) => {
      this.disableSaveButton.set(status === 'INVALID');
    });
  }

  isInvalid(): boolean {
    return this.taxNewsForm.invalid || this.activeRichEditor.isEmpty;
  }
}
